import React, { useState, Component } from 'react';
import { motion } from 'framer-motion';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend, LineChart, Line, ComposedChart } from 'recharts';
import { Zap, Wind, Sun, TrendingUp, TrendingDown, Activity, ArrowRight, Download, RefreshCw, Calendar, Battery, AlertTriangle, Factory } from 'lucide-react';
// --- Mock Data ---
const hourlyData = Array.from({
  length: 24
}, (_, i) => {
  const hour = i;
  const isPeak = hour >= 8 && hour <= 20;
  const isSolarPeak = hour >= 10 && hour <= 16;
  const baseLoad = 45 + Math.random() * 5;
  const load = baseLoad + (isPeak ? 15 : 0) + Math.random() * 5;
  const wind = 15 + Math.sin(hour / 4) * 5 + Math.random() * 3;
  const solar = isSolarPeak ? 20 + Math.random() * 5 : 0;
  const conventional = Math.max(0, load - wind - solar);
  const price = 60 + load / 60 * 40 - (wind + solar) * 0.8 + Math.random() * 10;
  return {
    time: `${hour}:00`,
    price: Math.round(price),
    load: Math.round(load),
    wind: Math.round(wind),
    solar: Math.round(solar),
    conventional: Math.round(conventional),
    frequency: 50 + (Math.random() * 0.1 - 0.05)
  };
});
const crossBorderFlows = [{
  country: 'France',
  flow: -2.5,
  type: 'import'
}, {
  country: 'Netherlands',
  flow: 1.2,
  type: 'export'
}, {
  country: 'Switzerland',
  flow: 3.1,
  type: 'export'
}, {
  country: 'Poland',
  flow: -1.8,
  type: 'import'
}, {
  country: 'Denmark',
  flow: -0.9,
  type: 'import'
}];
const gridEvents = [{
  id: 1,
  time: '08:30',
  type: 'warning',
  message: 'Frequency deviation detected (49.95Hz)'
}, {
  id: 2,
  time: '10:15',
  type: 'info',
  message: 'Solar ramp-up exceeding forecast by 5%'
}, {
  id: 3,
  time: '14:00',
  type: 'success',
  message: 'North-South link capacity restored'
}];
// --- Components ---
const CustomTooltip = ({
  active,
  payload,
  label
}: any) => {
  if (active && payload && payload.length) {
    return <div className="bg-white/95 backdrop-blur-xl border border-blue-100 rounded-xl p-4 shadow-xl z-50" data-id="element-1992">
        <p className="text-xs font-bold text-slate-900 mb-2" data-id="element-1993">{label}</p>
        <div className="space-y-1" data-id="element-1994">
          {payload.map((entry: any) => <div key={entry.name} className="flex items-center justify-between gap-4" data-id="element-1995">
              <div className="flex items-center gap-2" data-id="element-1996">
                <div className="w-2 h-2 rounded-full" style={{
              backgroundColor: entry.color
            }} data-id="element-1997" />
                <span className="text-xs text-slate-600 capitalize" data-id="element-1998">
                  {entry.name}
                </span>
              </div>
              <span className="text-xs font-mono font-medium text-slate-900" data-id="element-1999">
                {typeof entry.value === 'number' ? entry.value.toFixed(1) : entry.value}
                {entry.unit || ''}
              </span>
            </div>)}
        </div>
      </div>;
  }
  return null;
};
export function GermanyPricingDashboard() {
  const [selectedTimeframe, setSelectedTimeframe] = useState<'day-ahead' | 'intraday'>('day-ahead');
  // Separate datasets for each timeframe
  const dayAheadData = [{
    hour: '00:00',
    price: 45.2
  }, {
    hour: '03:00',
    price: 38.5
  }, {
    hour: '06:00',
    price: 52.8
  }, {
    hour: '09:00',
    price: 68.4
  }, {
    hour: '12:00',
    price: 75.2
  }, {
    hour: '15:00',
    price: 82.6
  }, {
    hour: '18:00',
    price: 95.3
  }, {
    hour: '21:00',
    price: 71.8
  }];
  const intradayData = [{
    hour: '00:00',
    price: 48.5
  }, {
    hour: '03:00',
    price: 42.1
  }, {
    hour: '06:00',
    price: 55.3
  }, {
    hour: '09:00',
    price: 72.8
  }, {
    hour: '12:00',
    price: 78.9
  }, {
    hour: '15:00',
    price: 85.2
  }, {
    hour: '18:00',
    price: 98.7
  }, {
    hour: '21:00',
    price: 68.4
  }];
  // Select data based on timeframe
  const chartData = selectedTimeframe === 'day-ahead' ? dayAheadData : intradayData;
  return <div className="space-y-6" data-id="element-2000">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-2001">
        <div data-id="element-2002">
          <div className="flex items-center gap-2 mb-1" data-id="element-2003">
            <div className="p-2 bg-blue-600 rounded-lg" data-id="element-2004">
              <Factory size={20} className="text-white" data-id="element-2005" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" data-id="element-2006">
              Germany Pricing Dashboard
            </h1>
          </div>
          <p className="text-slate-500 text-sm md:text-base ml-11" data-id="element-2007">
            Day-ahead market analysis, renewable integration, and grid stability
          </p>
        </div>

        <div className="flex items-center gap-3 ml-11 md:ml-0" data-id="element-2008">
          <button className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors shadow-sm" data-id="element-2009">
            <Calendar size={16} data-id="element-2010" />
            <span data-id="element-2011">Today, Oct 24</span>
          </button>
          <button className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm shadow-blue-500/20" data-id="element-2012">
            <RefreshCw size={16} data-id="element-2013" />
            <span data-id="element-2014">Refresh Data</span>
          </button>
        </div>
      </div>

      {/* Key Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2015">
        {[{
        title: 'Day-Ahead Price',
        value: '€84.50',
        unit: '/MWh',
        change: '+2.4%',
        trend: 'up',
        icon: Zap,
        color: 'blue'
      }, {
        title: 'Grid Frequency',
        value: '49.98',
        unit: 'Hz',
        change: '-0.02%',
        trend: 'down',
        icon: Activity,
        color: 'emerald'
      }, {
        title: 'Renewable Share',
        value: '42.8',
        unit: '%',
        change: '+5.1%',
        trend: 'up',
        icon: Wind,
        color: 'cyan'
      }, {
        title: 'Net Export',
        value: '2.4',
        unit: 'GW',
        change: '-1.2%',
        trend: 'down',
        icon: ArrowRight,
        color: 'indigo'
      }].map((metric, idx) => <motion.div key={metric.title} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: idx * 0.1
      }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-5 shadow-sm hover:shadow-md transition-all group" data-id="element-2016">
            <div className="flex justify-between items-start mb-4" data-id="element-2017">
              <div className={`p-2 rounded-lg bg-${metric.color}-50 text-${metric.color}-600 group-hover:bg-${metric.color}-100 transition-colors`} data-id="element-2018">
                <metric.icon size={20} data-id="element-2019" />
              </div>
              <div className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${metric.trend === 'up' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`} data-id="element-2020">
                {metric.trend === 'up' ? <TrendingUp size={12} data-id="element-2021" /> : <TrendingDown size={12} data-id="element-2022" />}
                {metric.change}
              </div>
            </div>
            <div data-id="element-2023">
              <p className="text-sm text-slate-500 font-medium" data-id="element-2024">
                {metric.title}
              </p>
              <div className="flex items-baseline gap-1 mt-1" data-id="element-2025">
                <span className="text-2xl font-bold text-slate-900" data-id="element-2026">
                  {metric.value}
                </span>
                <span className="text-sm text-slate-400 font-medium" data-id="element-2027">
                  {metric.unit}
                </span>
              </div>
            </div>
          </motion.div>)}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6" data-id="element-2028">
        <div className="flex items-center justify-between mb-6" data-id="element-2029">
          <div data-id="element-2030">
            <h3 className="text-lg font-semibold text-slate-900" data-id="element-2031">
              Hourly Pricing
            </h3>
            <p className="text-sm text-slate-500 mt-1" data-id="element-2032">
              Germany electricity market prices
            </p>
          </div>
          <div className="flex gap-2" data-id="element-2033">
            {['Day-Ahead', 'Intraday'].map(t => <button key={t} onClick={() => setSelectedTimeframe(t.toLowerCase() as 'day-ahead' | 'intraday')} className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${selectedTimeframe === t.toLowerCase() ? 'bg-blue-600 text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-2034">
                {t}
              </button>)}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={300} data-id="element-2035">
          <LineChart data={chartData} data-id="element-2036">
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" data-id="element-2037" />
            <XAxis dataKey="hour" stroke="#64748b" style={{
            fontSize: '12px'
          }} data-id="element-2038" />
            <YAxis stroke="#64748b" style={{
            fontSize: '12px'
          }} data-id="element-2039" />
            <Tooltip contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            fontSize: '12px'
          }} data-id="element-2040" />
            <Line type="monotone" dataKey="price" stroke="#3b82f6" strokeWidth={2} dot={{
            fill: '#3b82f6',
            r: 4
          }} data-id="element-2041" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2042">
        {/* Cross Border Flows */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.6
      }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm" data-id="element-2043">
          <div className="mb-4" data-id="element-2044">
            <h3 className="text-lg font-bold text-slate-900" data-id="element-2045">
              Cross-Border Flows
            </h3>
            <p className="text-sm text-slate-500" data-id="element-2046">
              Net import/export positions (GW)
            </p>
          </div>
          <div className="space-y-4" data-id="element-2047">
            {crossBorderFlows.map(flow => <div key={flow.country} className="flex items-center justify-between" data-id="element-2048">
                <div className="flex items-center gap-3" data-id="element-2049">
                  <div className={`w-2 h-8 rounded-full ${flow.flow > 0 ? 'bg-emerald-500' : 'bg-rose-500'}`} data-id="element-2050" />
                  <div data-id="element-2051">
                    <p className="text-sm font-medium text-slate-900" data-id="element-2052">
                      {flow.country}
                    </p>
                    <p className="text-xs text-slate-500 capitalize" data-id="element-2053">
                      {flow.type}
                    </p>
                  </div>
                </div>
                <div className="text-right" data-id="element-2054">
                  <p className={`text-sm font-bold ${flow.flow > 0 ? 'text-emerald-600' : 'text-rose-600'}`} data-id="element-2055">
                    {flow.flow > 0 ? '+' : ''}
                    {flow.flow} GW
                  </p>
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* Grid Stability */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.7
      }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm" data-id="element-2056">
          <div className="mb-4" data-id="element-2057">
            <h3 className="text-lg font-bold text-slate-900" data-id="element-2058">
              Grid Stability Events
            </h3>
            <p className="text-sm text-slate-500" data-id="element-2059">Real-time system alerts</p>
          </div>
          <div className="space-y-3" data-id="element-2060">
            {gridEvents.map(event => <div key={event.id} className="flex gap-3 p-3 bg-white/50 rounded-lg border border-slate-100" data-id="element-2061">
                <div className={`mt-0.5 ${event.type === 'warning' ? 'text-amber-500' : event.type === 'success' ? 'text-emerald-500' : 'text-blue-500'}`} data-id="element-2062">
                  {event.type === 'warning' ? <AlertTriangle size={16} data-id="element-2063" /> : event.type === 'success' ? <Zap size={16} data-id="element-2064" /> : <Activity size={16} data-id="element-2065" />}
                </div>
                <div data-id="element-2066">
                  <div className="flex items-center gap-2 mb-1" data-id="element-2067">
                    <span className="text-xs font-bold text-slate-700" data-id="element-2068">
                      {event.time}
                    </span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium uppercase ${event.type === 'warning' ? 'bg-amber-50 text-amber-700' : event.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'}`} data-id="element-2069">
                      {event.type}
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed" data-id="element-2070">
                    {event.message}
                  </p>
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* Renewable Forecast */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.8
      }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm flex flex-col" data-id="element-2071">
          <div className="mb-4" data-id="element-2072">
            <h3 className="text-lg font-bold text-slate-900" data-id="element-2073">
              Renewable Forecast
            </h3>
            <p className="text-sm text-slate-500" data-id="element-2074">
              Wind & Solar deviation risk
            </p>
          </div>
          <div className="flex-1 flex items-center justify-center" data-id="element-2075">
            <ResponsiveContainer width="100%" height={200} data-id="element-2076">
              <BarChart data={hourlyData.filter((_, i) => i % 4 === 0)} layout="vertical" data-id="element-2077">
                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#e2e8f0" data-id="element-2078" />
                <XAxis type="number" hide data-id="element-2079" />
                <YAxis dataKey="time" type="category" width={40} tick={{
                fontSize: 11,
                fill: '#64748b'
              }} axisLine={false} tickLine={false} data-id="element-2080" />
                <Tooltip cursor={{
                fill: 'transparent'
              }} content={<CustomTooltip data-id="element-2082" />} data-id="element-2081" />
                <Bar dataKey="wind" name="Wind Forecast" fill="#0ea5e9" radius={[0, 4, 4, 0]} barSize={8} data-id="element-2083" />
                <Bar dataKey="solar" name="Solar Forecast" fill="#eab308" radius={[0, 4, 4, 0]} barSize={8} data-id="element-2084" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 pt-4 border-t border-slate-200/60" data-id="element-2085">
            <div className="flex justify-between items-center text-sm" data-id="element-2086">
              <span className="text-slate-600" data-id="element-2087">Forecast Confidence</span>
              <span className="font-bold text-emerald-600" data-id="element-2088">High (92%)</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>;
}